// LiveChat keeps a conversation up to date without reloading.
//
// It holds an EventSource open against the thread's SSE endpoint and appends each
// bubble the server pushes. Sending is untouched — that is still an HTMX form
// POST — so this only supplies the half that was missing: being told that the
// other side said something.
//
// Deduping is by message id, not by "ignore my own role": the sender's bubble is
// already appended by the POST response, and the same person may have the thread
// open in two tabs. An id that is already on screen is simply dropped.
//
// EventSource reconnects on its own after a drop, so there is no retry logic here
// — only a visible hint when the connection is down, so a customer does not sit
// there believing the silence means nobody is answering.
export class LiveChat extends HTMLElement {
  private source: EventSource | null = null;

  connectedCallback(): void {
    const url = this.getAttribute("stream");
    const logSelector = this.getAttribute("log");
    if (!url || !logSelector) return;

    const log = document.querySelector<HTMLElement>(logSelector);
    if (!log) return;

    this.watchForDuplicates(log);
    this.source = new EventSource(url);

    this.source.addEventListener("message", (event: MessageEvent<string>): void => {
      this.appendBubble(log, event.data);
      this.setState("live");
    });

    this.source.addEventListener("open", (): void => this.setState("live"));

    // Fires on every drop, including the ones EventSource then recovers from, so
    // it is a hint rather than an error.
    this.source.addEventListener("error", (): void => this.setState("reconnecting"));
  }

  disconnectedCallback(): void {
    this.source?.close();
    this.source = null;
  }

  /**
   * Drops any bubble whose id is already on screen, whoever put it there.
   *
   * The sender's own message arrives twice: once as the HTMX response to their
   * POST, and once down the stream. Checking before appending is not enough —
   * the two race, and when the stream wins, HTMX appends the duplicate
   * afterwards and never asks us. So the log polices itself: first id wins.
   */
  private watchForDuplicates(log: HTMLElement): void {
    new MutationObserver((records): void => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (!(node instanceof HTMLElement)) continue;
          const id = node.dataset["msgId"];
          if (!id) continue;
          const all = log.querySelectorAll(`[data-msg-id="${CSS.escape(id)}"]`);
          for (let i = 1; i < all.length; i++) {
            all[i]?.remove(); // keep the first, drop the rest
          }
        }
      }
    }).observe(log, { childList: true });
  }

  private appendBubble(log: HTMLElement, html: string): void {
    const template = document.createElement("template");
    template.innerHTML = html.trim();
    const bubble = template.content.firstElementChild as HTMLElement | null;
    if (!bubble) return;

    const id = bubble.dataset["msgId"];
    // Already on screen: our own echo, or a second tab that got there first.
    if (id && log.querySelector(`[data-msg-id="${CSS.escape(id)}"]`)) return;

    // The "no messages yet" placeholder is no longer true.
    log.querySelector("[data-chat-empty], #chat-empty, #cust-chat-empty")?.remove();

    // Only follow the conversation if the reader is already at the bottom —
    // yanking the scroll while they are reading back through history is rude.
    const atBottom = log.scrollHeight - log.scrollTop - log.clientHeight < 80;
    log.append(bubble);
    if (atBottom) log.scrollTop = log.scrollHeight;
  }

  private setState(state: "live" | "reconnecting"): void {
    const badge = this.querySelector<HTMLElement>("[data-chat-state]");
    if (!badge) return;
    badge.dataset["state"] = state;
    badge.textContent = state === "live" ? "En vivo" : "Reconectando…";
  }
}
